import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  DollarSign, Calendar, TrendingUp, Plus, Users, Ticket, Gift,
  ArrowUpRight, ArrowRight, BarChart3, Sparkles, Clock, Star
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerLayout } from '@/components/PartnerLayout';
import api from '@/lib/api';

const QuickStatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'indigo' }) => {
  const colorClasses = {
    indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-950 dark:text-purple-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-950 dark:text-orange-400',
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
          {trend && (
            <Badge variant="secondary" className="bg-green-100 text-green-700 border-0">
              <ArrowUpRight className="w-3 h-3 mr-1" />
              {trend}%
            </Badge>
          )}
        </div>
        <div className="mt-4">
          <p className="text-3xl font-bold text-foreground">{value}</p>
          <p className="text-sm text-muted-foreground mt-1">{title}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
};

const QuickAction = ({ icon: Icon, title, description, href, color = 'indigo' }) => {
  const colorClasses = {
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    green: 'bg-green-600 hover:bg-green-700',
  };

  return (
    <Link to={href}>
      <Card className="hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer group">
        <CardContent className="p-6">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white mb-4 ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
          <h3 className="font-bold text-foreground group-hover:text-indigo-600 transition-colors">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
          <div className="flex items-center gap-1 mt-3 text-sm text-indigo-600 font-medium">
            Get started <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPartner, user, loading: authLoading, refreshUser } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      if (authLoading) return;
      
      if (!isAuthenticated) {
        navigate('/auth');
        return;
      }
      
      if (!user) {
        try {
          await refreshUser();
        } catch (error) {
          navigate('/auth');
          return;
        }
      }
      
      // Allow both partners and admins to access
      if (!isPartner && user?.role !== 'admin') {
        navigate('/dashboard');
        return;
      }
      
      fetchData();
    };
    
    init();
  }, [isAuthenticated, isPartner, authLoading, user]);

  const fetchData = async () => {
    try {
      const [dashboardRes, reservationsRes] = await Promise.all([
        api.get('/partner/dashboard'),
        api.get('/partner/reservations').catch(() => ({ data: [] }))
      ]);
      setDashboard(dashboardRes.data);
      setReservations(reservationsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated || !isPartner) return null;

  const upcomingReservations = reservations
    .filter(r => new Date(r.date) >= new Date())
    .slice(0, 5);

  return (
    <PartnerLayout 
      title={`Welcome back, ${user?.full_name?.split(' ')[0] || 'Partner'}!`}
      subtitle="Here's what's happening with your business today"
      actions={
        <div className="flex gap-3">
          <Button 
            onClick={() => navigate('/partner/experiences')}
            variant="outline"
            className="border-purple-200 text-purple-600 hover:bg-purple-50"
          >
            <Gift className="w-4 h-4 mr-2" />
            Create Experience
          </Button>
          <Button 
            onClick={() => navigate('/partner/events')}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Event
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <QuickStatCard
              title="Total Revenue"
              value={`$${dashboard?.total_revenue?.toFixed(2) || '0.00'}`}
              subtitle="All time earnings"
              icon={DollarSign}
              trend={12}
              color="green"
            />
            <QuickStatCard
              title="Tickets Sold"
              value={dashboard?.total_tickets_sold || 0}
              subtitle="This month"
              icon={Ticket}
              trend={8}
              color="indigo"
            />
            <QuickStatCard
              title="Active Events"
              value={dashboard?.total_events || 0}
              subtitle="Currently live"
              icon={Calendar}
              color="purple"
            />
            <QuickStatCard
              title="Reservations"
              value={upcomingReservations.length}
              subtitle="Upcoming"
              icon={Users}
              color="orange"
            />
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="text-lg font-bold text-foreground mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <QuickAction
                icon={Gift}
                title="Create New Experience"
                description="Set up vouchers and exclusive offers"
                href="/partner/experiences"
                color="purple"
              />
              <QuickAction
                icon={Plus}
                title="Create New Event"
                description="Set up a new event and start selling tickets"
                href="/partner/events"
                color="indigo"
              />
              <QuickAction
                icon={Sparkles}
                title="AI Marketing"
                description="Generate professional marketing content instantly"
                href="/partner/ai-marketing"
                color="green"
              />
            </div>
          </div>

          {/* Experiences Overview - PRIORITIZED */}
          <Card className="border-2 border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50/50 to-pink-50/50 dark:from-purple-950/20 dark:to-pink-950/20">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Gift className="w-6 h-6 text-purple-600" />
                  Your Experiences
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Experiences are your primary revenue driver - create compelling offers!
                </p>
              </div>
              <Button 
                onClick={() => navigate('/partner/experiences')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Experience
              </Button>
            </CardHeader>
            <CardContent>
              {dashboard?.experiences?.length === 0 ? (
                <div className="text-center py-12">
                  <Gift className="w-16 h-16 text-purple-300 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">No Experiences Yet</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Experiences generate 30% platform fees but often have higher margins. 
                    Create your first experience to start earning!
                  </p>
                  <Button 
                    onClick={() => navigate('/partner/experiences')}
                    size="lg"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Create Your First Experience
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboard?.experiences?.slice(0, 6).map((exp) => (
                    <div
                      key={exp.id}
                      className="p-4 bg-white dark:bg-gray-900 border rounded-lg hover:border-purple-500/50 hover:shadow-md transition-all cursor-pointer group"
                      onClick={() => navigate(`/partner/experiences/${exp.id}`)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-950 flex items-center justify-center">
                          <Gift className="w-5 h-5 text-purple-600" />
                        </div>
                        {exp.gold_price && (
                          <Badge className="bg-yellow-400 text-yellow-900 text-xs">
                            <Star className="w-3 h-3 mr-1" />
                            Gold
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-bold text-foreground group-hover:text-purple-600 transition-colors line-clamp-1">
                        {exp.title}
                      </h4>
                      <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                        {exp.venue_name}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-purple-600">${exp.price}</span>
                        <Badge variant="secondary" className="text-xs">
                          {exp.sold || 0} sold
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Events */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg">Your Events</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/partner/events')}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  {dashboard?.events?.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">No events created yet</p>
                      <Button onClick={() => navigate('/partner/events')}>
                        Create Your First Event
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {dashboard?.events?.slice(0, 5).map((event) => (
                        <div
                          key={event.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:border-indigo-600/50 transition-colors cursor-pointer group"
                          onClick={() => navigate(`/partner/events/${event.id}`)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center text-indigo-600">
                              <Calendar className="w-6 h-6" />
                            </div>
                            <div>
                              <h4 className="font-bold text-foreground group-hover:text-indigo-600 transition-colors">
                                {event.title}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {event.date} • {event.venue_name}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="secondary">
                                {event.tickets_sold} / {event.capacity}
                              </Badge>
                            </div>
                            <p className="text-sm font-bold text-foreground">
                              ${(event.tickets_sold * event.price).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Reservations */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Upcoming Reservations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcomingReservations.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No upcoming reservations</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {upcomingReservations.map((res) => (
                      <div key={res.id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-foreground">{res.user_name}</span>
                          <Badge variant="outline">{res.party_size} guests</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {res.date} at {res.time}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {res.venue_name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Performance Tip */}
          <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-lg mb-2">💡 Pro Tip</h3>
                  <p className="text-white/90 max-w-xl">
                    Events with AI-generated descriptions see 40% more engagement. 
                    Try our AI Marketing Suite to create compelling content for your next event!
                  </p>
                </div>
                <Button 
                  className="bg-white text-indigo-600 hover:bg-white/90"
                  onClick={() => navigate('/partner/ai-marketing')}
                >
                  Try AI Marketing
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PartnerLayout>
  );
};

export default PartnerDashboard;
